import { useEffect, useRef, useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import axios from 'axios';
import { API_URL } from '../../common/Constants.js';
import Cookies from '../../common/Cookies.js';
import CompletePaymentDialog from '../account/CompletePaymentDialog';

/** Routes owners may open without creating an agent first (e.g. billing, team). */
const PATHS_ALLOWED_WITHOUT_AGENT = new Set(['/AccountSettings', '/Logout', '/agent-creation']);

function isChatLogAccessOnlyUser() {
  const token = Cookies.getJwtToken();
  if (!token) return false;
  try {
    const d = jwtDecode(token);
    return d.chat_log_access_only === true || d.chat_log_access_only === 'true';
  } catch {
    return false;
  }
}

/** Paths chat_log_access_only users may use (hash router paths, no # prefix). */
function isPathAllowedForChatLogUser(path) {
  if (path === '/Logout') return true;
  if (path === '/SystemAlerts') return true;
  if (path === '/Dashboard' || path.startsWith('/Dashboard/')) return true;
  if (path === '/chat-logs' || path.startsWith('/chat-logs/')) return true;
  return false;
}

function getPathnameNow(locationPathname) {
  const fromHash = (window.location.hash || '').replace(/^#/, '').split('?')[0];
  if (fromHash) return fromHash || '/';
  return locationPathname || '/';
}

export default function Layout({ loggedIn, setLoggedIn }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pendingPlanChecked = useRef(false);
  const [paymentGateOpen, setPaymentGateOpen] = useState(false);
  const dismissPaymentGate = useCallback(() => setPaymentGateOpen(false), []);

  useEffect(() => {
    const isOnLogin = location.pathname === '/Login';
    const params = new URLSearchParams(location.search);
    const hasNext = !!params.get('next');
    if (!loggedIn && !location.pathname.includes("Signup") && !isOnLogin) {
      const target = window.location.hash && window.location.hash !== "" ? window.location.hash : (location.pathname + location.search);
      navigate(`/Login?next=${encodeURIComponent(target)}`);
      return;
    }

    if (!loggedIn && isOnLogin && hasNext) {
      return;
    }

    function isTokenExpired(token) {
      if (!token) return true;
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        return decodedToken.exp < currentTime;
      } catch (error) {
        return true;
      }
    }

    if (isTokenExpired(Cookies.getJwtToken()) && !isOnLogin && !hasNext) {
      Cookies.setJwtToken('');
      setLoggedIn(false);
      const target = window.location.hash && window.location.hash !== "" ? window.location.hash : (location.pathname + location.search);
      navigate(`/Login?next=${encodeURIComponent(target)}`);
      return;
    }

    if (!loggedIn) return;

    const chatLogOnly = isChatLogAccessOnlyUser();
    const currentPathForRole = getPathnameNow(location.pathname);
    if (chatLogOnly && !isPathAllowedForChatLogUser(currentPathForRole)) {
      navigate('/Dashboard', { replace: true });
      return;
    }

    const isOnCompletePayment = getPathnameNow(location.pathname) === '/complete-payment';

    // Payment gate: check for pending_plan before allowing agent-creation.
    // Only fetch once per session to avoid repeated API calls on every route change.
    if (!pendingPlanChecked.current && !isOnCompletePayment) {
      pendingPlanChecked.current = true;
      axios
        .get(`${API_URL}/account/pending-plan`)
        .then((res) => {
          const pathNow = getPathnameNow(location.pathname);
          if (res.data.success && res.data.pendingPlan) {
            setPaymentGateOpen(true);
          } else if (
            !isChatLogAccessOnlyUser() &&
            Cookies.getCookies('hasAgent') === 'false' &&
            !PATHS_ALLOWED_WITHOUT_AGENT.has(pathNow)
          ) {
            navigate('/agent-creation');
          }
        })
        .catch(() => {
          const pathNow = getPathnameNow(location.pathname);
          if (
            !isChatLogAccessOnlyUser() &&
            Cookies.getCookies('hasAgent') === 'false' &&
            !PATHS_ALLOWED_WITHOUT_AGENT.has(pathNow)
          ) {
            navigate('/agent-creation');
          }
        });
      return;
    }

    if (isOnCompletePayment) return;

    if (paymentGateOpen) return;

    const currentPath = getPathnameNow(location.pathname);
    if (
      !isChatLogAccessOnlyUser() &&
      Cookies.getCookies('hasAgent') === 'false' &&
      !PATHS_ALLOWED_WITHOUT_AGENT.has(currentPath)
    ) {
      navigate('/agent-creation');
    }
  }, [loggedIn, location, navigate, paymentGateOpen]);

  const isCompletePaymentRoute = getPathnameNow(location.pathname) === '/complete-payment';

  return (
    <div>
      {!isCompletePaymentRoute && (
        <CompletePaymentDialog open={paymentGateOpen} onNoPendingPlan={dismissPaymentGate} />
      )}
      <Outlet />
    </div>
  );
}
